export const navItems = [
  { href: "/", label: "Overview" },
  { href: "/patterns", label: "Patterns" },
  { href: "/protocol", label: "MCP" },
  { href: "/case-study", label: "Case study" },
  { href: "/design-system", label: "Design" }
];

export const patternPackets = [
  {
    id: "boundary-leak",
    title: "Anti-pattern: Boundary leak",
    status: "avoid",
    severity: "critical",
    category: "boundaries",
    uri: "resource://patterns/boundaries/boundary-leak",
    summary:
      "Domain behavior is pulled into HTTP handlers, background jobs, ORM callbacks, or UI-specific data structures until the system cannot be tested without the outer framework.",
    tags: ["boundaries", "domain", "adapter", "testability", "modularity"],
    bad: `app.post("/invoice", async (req, res) => {
  const user = await db.user.find(req.body.userId);
  if (user.plan === "free" && req.body.total > 100) {
    return res.status(403).json({ error: "limit" });
  }
  await stripe.charge(req.body);
});`,
    good: `const decision = billingPolicy.canInvoice({
  plan: user.plan,
  total: command.total
});

if (!decision.allowed) {
  return presenter.forbidden(decision.reason);
}`,
    sideEffects: [
      "Slow tests because business rules require web and database boot.",
      "Duplicated rules in jobs, controllers, and event handlers.",
      "Framework migration becomes a rewrite instead of an adapter swap."
    ],
    verification: [
      "Trace one business rule from input to storage.",
      "Check whether the rule can be unit-tested without HTTP, ORM, or queue setup.",
      "List every adapter that knows the rule."
    ],
    checklist:
      "Trace one business rule. Verify it is independent from HTTP, ORM, queue, and presentation concerns. Record every adapter that knows the rule.",
    sources: [{ label: "Parnas", href: "http://sunnyday.mit.edu/16.355/parnas-criteria.html" }]
  },
  {
    id: "contract-first-port",
    title: "Pattern: Contract-first port",
    status: "required",
    severity: "structural",
    category: "boundaries",
    uri: "resource://patterns/boundaries/contract-first-port",
    summary:
      "External capabilities enter the core through explicit interfaces named after domain needs, not vendor APIs.",
    tags: ["ports", "adapters", "dependency inversion", "vendor swaps", "testing"],
    good:
      "If the core says Stripe, S3, or Express, the dependency points the wrong way. The core should say PaymentGateway, ArtifactStore, or RequestContext.",
    sideEffects: [
      "Vendor-specific errors leak into domain decisions.",
      "Tests need network or framework setup for business behavior.",
      "Adapters become hard to replace safely."
    ],
    verification: [
      "Find imports from domain code into infrastructure packages.",
      "Check whether adapters implement a small contract owned by the core.",
      "Confirm failure semantics are expressed in domain language."
    ],
    checklist:
      "Search domain code for vendor/framework imports. Replace direct calls with a small port named after the domain capability.",
    sources: [{ label: "Service Layer", href: "https://martinfowler.com/eaaCatalog/serviceLayer.html" }]
  },
  {
    id: "test-hourglass",
    title: "Anti-pattern: Test hourglass",
    status: "avoid",
    severity: "high",
    category: "testing strategy",
    uri: "resource://patterns/testing/test-hourglass",
    summary:
      "The suite has many unit tests and many end-to-end tests but almost no focused contract, integration, or component tests at the boundaries where regressions happen.",
    tags: ["testing", "test pyramid", "pyramid", "e2e", "flaky test", "flaky tests", "contract test", "slow tests"],
    diagram: `bad hourglass                 better distribution

E2E      ########             E2E      ##
Contract                      Contract ######
Component                     Component #######
Unit     ########             Unit     #########`,
    sideEffects: [
      "Flaky release confidence because slow flows carry too much responsibility.",
      "Hidden boundary defects because mocks skip serialization and integration.",
      "Agents over-report tested after running the cheapest green subset."
    ],
    verification: [
      "Count tests by layer and average runtime.",
      "Map the last five bugs to the layer that would have caught them fastest.",
      "Add one contract or component evidence gate before adding another E2E flow."
    ],
    checklist:
      "Count tests by layer and runtime. Map recent defects to the cheapest layer that should catch them. Add boundary-level contract or component evidence before adding more end-to-end coverage.",
    sources: [
      { label: "Fowler", href: "https://martinfowler.com/bliki/TestPyramid.html" },
      {
        label: "Google Testing Blog",
        href: "https://testing.googleblog.com/2015/04/just-say-no-to-more-end-to-end-tests.html"
      }
    ]
  },
  {
    id: "requirement-lens",
    title: "Pattern: Requirement lens",
    status: "recommended",
    severity: "medium",
    category: "requirements",
    uri: "resource://patterns/requirements/requirement-lens",
    summary:
      "Architecture guidance is tied to a named requirement, scenario, or business invariant so reviewers can tell which user or business need a boundary protects.",
    tags: ["requirements", "traceability", "founder", "domain story", "coverage"],
    good:
      "Use stable IDs such as BR-BILLING-0041 in cases, tests, and agent verification reports. Keep requirements readable as Markdown, then index them for search and coverage checks.",
    sideEffects: [
      "Decisions become hard to explain to founders or future agents.",
      "Tests pass while protecting no named user or business need.",
      "Refactors lose the reason a boundary exists."
    ],
    verification: [
      "Pick a high-risk behavior and find the requirement ID.",
      "Find tests or manual checks that cite the ID.",
      "Find the architecture rule that protects the behavior."
    ],
    checklist:
      "Pick one high-risk behavior. Assign a stable requirement ID. Link the ID to tests, architecture rules, and the evidence that proves the behavior.",
    sources: [{ label: "See case map", href: "/case-study#requirements-map" }]
  },
  {
    id: "observable-failure-contract",
    title: "Pattern: Observable failure contract",
    status: "required",
    severity: "operational",
    category: "operations",
    uri: "resource://patterns/operations/observable-failure-contract",
    summary:
      "Every critical operation defines what failure looks like, how the system reports it, and which signal proves user impact or recovery.",
    tags: ["observability", "slo", "errors", "logs", "metrics", "tracing", "safe context"],
    good:
      "Define structured errors, retry bounds, idempotency behavior, user-visible states, logs, metrics, and traces before production.",
    sideEffects: [
      "Incidents require folklore and log archaeology.",
      "Retries create duplicate side effects or unclear user states.",
      "Teams cannot tell whether users were affected."
    ],
    verification: [
      "Force the downstream dependency to fail.",
      "Confirm logs contain a stable operation ID and safe context.",
      "Confirm a metric or trace shows the affected user path."
    ],
    checklist:
      "Force a dependency failure. Pass only if user-facing state, structured error, metric, and trace identify the operation without leaking secrets. Attach the observed log, metric, or trace evidence.",
    sources: [{ label: "Google SRE SLOs", href: "https://sre.google/sre-book/service-level-objectives/" }]
  },
  {
    id: "agent-handoff-drift",
    title: "Anti-pattern: Agent handoff drift",
    status: "avoid",
    severity: "medium",
    category: "AI-agent maintainability",
    uri: "resource://patterns/ai-agent-maintainability/agent-handoff-drift",
    summary:
      "Architecture intent, test scope, and known risks live only in chat. The next agent starts from a vague prompt and silently loses the decisions that made the previous work safe.",
    tags: ["ai agents", "agent handoff evidence", "handoff", "memory", "evidence", "skills"],
    sideEffects: [
      "Repeated rediscovery of constraints and previous failures.",
      "Incorrect refactors because the agent cannot see boundary intent.",
      "Reports that say verified without durable evidence or risk notes."
    ],
    verification: [
      "Ask a new agent to explain the architecture constraints without reading chat.",
      "Check whether requirements, cases, and evidence have stable local IDs.",
      "Move accepted decisions into a skill, spec, or case file."
    ],
    checklist:
      "Ask a fresh agent to explain constraints without chat history. Pass only if requirements, cases, decisions, commands, evidence, and residual risks are recoverable from local files or stable resources.",
    sources: [{ label: "Install agent skill", href: "/protocol#agent-skills" }]
  }
];

export const searchRecords = [
  ...patternPackets.map((packet) => ({
    ...packet,
    url: `/patterns#${packet.id}`,
    resourceUri: packet.uri
  })),
  {
    id: "billing-case-study",
    title: "Case Study: AI-built billing backend",
    summary: "Before/after map for billing policy, ports, idempotency, and evidence gates.",
    url: "/case-study",
    resourceUri: "resource://case-studies/ai-built-billing",
    tags: ["case study", "billing", "requirements", "idempotency", "founder"]
  },
  {
    id: "mcp-surface",
    title: "MCP Surface",
    summary: "Resources, prompts, and tools for exposing architecture packets to agents.",
    url: "/protocol",
    resourceUri: "resource://protocol/mcp-surface",
    tags: ["mcp", "resources", "tools", "prompts", "agents"]
  }
];

export const protocolExamples = {
  resources: {
    label: "resources/list",
    code: {
      jsonrpc: "2.0",
      id: 1,
      method: "resources/list"
    }
  },
  prompts: {
    label: "prompts/get",
    code: {
      jsonrpc: "2.0",
      id: 2,
      method: "prompts/get",
      params: {
        name: "review_architecture",
        arguments: {
          patternUri: "resource://patterns/boundaries/boundary-leak",
          codeContext: "Paste relevant files or a repository summary"
        }
      }
    }
  },
  tools: {
    label: "tools/call",
    code: {
      jsonrpc: "2.0",
      id: 3,
      method: "tools/call",
      params: {
        name: "searchPatterns",
        arguments: {
          query: "flaky payment tests",
          status: ["avoid", "required"],
          includeExamples: true
        }
      }
    }
  }
};

export const pageDocs = [
  {
    title: "Overview",
    purpose: "Explain the product promise and route people by intent.",
    useCases: "Founder evaluation, developer onboarding, agent entry point discovery.",
    interactors: "Global search, primary navigation, packet preview links, source links."
  },
  {
    title: "Patterns",
    purpose: "Let readers locate a pattern or anti-pattern and act on it.",
    useCases: "Review code, plan refactor, explain side effects, copy verification steps.",
    interactors: "Search field, status filters, examples, copy checklist buttons."
  },
  {
    title: "MCP",
    purpose: "Show how the same knowledge becomes a protocol surface.",
    useCases: "Implement server, test agent calls, install skills, inspect JSON indexes.",
    interactors: "Endpoint links, request tabs, copy payload, skill install command."
  },
  {
    title: "Case Study",
    purpose: "Make abstract rules concrete through a system example.",
    useCases: "Diagnose AI-built code, plan sequence, map requirements to evidence.",
    interactors: "Architecture diagram, requirement table, ordered playbook."
  },
  {
    title: "Design",
    purpose: "Document principles, tokens, page docs, action mapping, and QA gates.",
    useCases: "Evaluate direction, extend pages, keep UI consistent.",
    interactors: "Token board, page docs, need-to-element table, critique checks."
  }
];
